import Dexie, { Table } from 'dexie';

import type { ObservabilityEventName } from '../observability.events';

/**
 * One row per (deduped) event. Common context fields are promoted to real
 * indexed columns so eviction and aggregation are indexed range queries, not
 * a full-table scan-and-parse -- see
 * `improvements/16-observability-error-handling.md`'s "Schema: wide indexed
 * columns, narrow blob" reasoning. Everything event-specific (operation,
 * message, documentHash, ...) lives in `payload`, which callers never query
 * directly.
 */
export interface ObservabilityEventRow {
	id?: number;
	name: ObservabilityEventName;
	appKey: string;
	template: string;
	version: string;
	buildNumber: number;
	swVersion: string;
	/** First-occurrence timestamp; not updated on repeat occurrences. */
	timestamp: number;
	/** `name + category`, per `local-reporter.ts`'s in-memory dedupe key. */
	dedupeKey: string;
	occurrences: number;
	payload: Record<string, unknown>;
	/** Approximate serialized size in bytes, cached at write time so the
	 * byte-budget eviction sweep doesn't have to re-stringify every row. */
	approxBytes: number;
}

export interface InternedDocumentRow {
	hash: string;
	text: string;
}

export type ObservabilityMetaKey = 'lastTtlSweepAt';

export interface ObservabilityMetaRow {
	key: ObservabilityMetaKey;
	value: number;
}

/**
 * Deliberately a database of its own, not a table on the existing
 * `DexieDb` (`src/shared/storage/db.ts`) -- that database's contract is
 * *explicit, user-initiated downloads* (favourites/library), and
 * observability events are the opposite: incidental, automatic, never
 * chosen by the user. See the topic file's "Storage" section for the full
 * reasoning.
 */
export class ObservabilityDb extends Dexie {
	events!: Table<ObservabilityEventRow, number>;
	documents!: Table<InternedDocumentRow, string>;
	meta!: Table<ObservabilityMetaRow, string>;

	constructor() {
		super('cf-observability');
		this.version(1).stores({
			events: '++id, name, appKey, template, version, buildNumber, timestamp, dedupeKey',
			documents: 'hash',
			meta: 'key',
		});
	}
}

export const observabilityDb = new ObservabilityDb();
